import React, { useState, useEffect, useRef } from "react";
import { Typography, Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Button from "../ButtonComponent/Button";
import apiService from "../../service";
import Toast from "../Common/CommonToast/CommonToast";
import Loader from "../Common/Loader";
import DrawerLogo from "../../assets/DrawerLogo.png";

function InviteUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const apiCalledRef = useRef(false); // To prevent multiple API calls

  const queryParams = new URLSearchParams(location.search);
  const registerParam = queryParams.get("registered");
  const register = registerParam === "true"; // convert to boolean

  // Call API only once on mount
  useEffect(() => {
    if (!apiCalledRef.current && id && register === false) {
      handleApiCallForRegistration(id);
      apiCalledRef.current = true;
    }
  });

  const handleApiCallForRegistration = async (inviteId) => {
    try {
      setLoading(true);
      const payload = { inviteToken: inviteId };
      const response = await apiService.getuserdetails(payload, false);
      console.log("Invite user response:", response);
      const userdata = response.data;
      if (userdata.invite?.registered === true) {
        setToast({
          open: true,
          message: "You have already registered. Redirecting to login...",
          severity: "info",
        });
        setTimeout(() => {
          navigate("/login", {
            state: {
              email: userdata.invite?.inviteeEmail || "",
              inviteId: inviteId,
              preFilledEmail: userdata.invite?.inviteeEmail || "",
            },
          });
        }, 200000);
      } else {
        navigate("/signup", {
          state: {
            inviteToken: inviteId,
            preFilledName: userdata.invite?.inviteeName || "",
            preFilledEmail: userdata.invite?.inviteeEmail || "",
            isInvite: true,
          },
        });
      }
    } catch (error) {
      console.error("Error checking invite status:", error);
      setError("Error with invitation.");
      setToast({
        open: true,
        message: "Invalid or expired invitation link",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!id) {
      setError("Invalid invitation link");
      return;
    }
    setOpen(true);
    try {
      setLoading(true);
      const payload = { inviteToken: id, status: "accept" };
      const response = await apiService.getuserdetails(payload, register);
      const userdata = response.data;
      setOpen(false);
      if (userdata.invite?.registered === true) {
        setToast({
          open: true,
          message: "You have already registered. Redirecting to login...",
          severity: "info",
        });
        setTimeout(() => {
          navigate("/login", {
            state: {
              email: userdata.invite?.inviteeEmail || "",
              inviteId: id,
              preFilledEmail: userdata.invite?.inviteeEmail || "",
            },
          });
        }, 2000);
      } else {
        navigate("/signup", {
          state: {
            inviteToken: id,
            preFilledName: userdata.invite?.inviteeName || "",
            preFilledEmail: userdata.invite?.inviteeEmail || "",
            isInvite: true,
          },
        });
      }
    } catch (error) {
      console.error("Error checking invite status:", error);
      setError(error.message);
      setToast({
        open: true,
        message: "Invalid or expired invitation link",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!id) {
      setToast({
        open: true,
        message: "Invalid invitation link",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const payload = { inviteToken: id, status: "reject" };
      await apiService.getuserdetails(payload, register);

      setToast({
        open: true,
        message: "You declined the invitation. Redirecting to login...",
        severity: "warning",
      });

      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      console.error("Error rejecting invite:", error);
      setToast({
        open: true,
        message: "Failed to decline invitation",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          padding: "20px",
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ mt: 2, color: "#333" }}>
          Processing your request...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          padding: "20px",
        }}
      >
        <Typography sx={{ mb: 2, color: "red" }}>{error}</Typography>
        <Button
          type={"button"}
          className="credit-button"
          label="Back to Login"
          onClick={() => navigate("/login")}
        />
      </Box>
    );
  }

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      {open && <Loader open={open} handleClose={handleClose} />}
      <Box className="maincontainer">
        <Box className="image-container">
          {/* <img src={SignupBg} alt="Background" className="background-image" /> */}
          <div className="bg-logo"></div>
        </Box>

        <Box className="right-section">
          <Box className="signup-box">
            <div>
              <img src={DrawerLogo} alt="Logo" className="logo" />
            </div>

            <Typography
              variant="h6"
              sx={{ color: "#333", mb: 3, mt: 3, fontSize: "18px" }}
            >
              You have been invited. Do you want to accept?
            </Typography>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                type={"button"}
                className="credit-button"
                text="Accept"
                onClick={handleAccept}
              />
              <Button
                type={"button"}
                className="decline-button"
                text="Decline"
                onClick={handleDecline}
                variant="outlined"
              />
            </Box>

            <Toast
              open={toast.open}
              message={toast.message}
              severity={toast.severity}
              onClose={handleCloseToast}
              autoHideDuration={3000}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default InviteUser;
